
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Divider,
  IconButton, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Tooltip, LinearProgress, FormControl,
  InputLabel, Select, MenuItem, InputAdornment
} from '@mui/material';
import {
  ArrowBack as BackIcon, LocalShipping as ShippingIcon, Cancel as CancelIcon,
  Print as PrintIcon, Business as BusinessIcon, Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon, AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon, Pending as PendingIcon, Info as InfoIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API;

const PurchaseOrderDetail = () => {
  const { po_id } = useParams();
  const navigate = useNavigate();
  const [po, setPO] = useState(null);
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount_paid: 0,
    payment_mode: 'cash',
    payment_notes: ''
  });
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    loadPO();
    loadGRNs();
  }, [po_id]);

  useEffect(() => {
    const handleFocus = () => loadPO();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const loadPO = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/purchase-orders/${po_id}`);
      setPO(response.data);
    } catch (error) {
      console.error('Error loading PO:', error);
      setError('Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };

  const loadGRNs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/purchase-orders/${po_id}/grns`);
      setGRNs(response.data.grns || []);
    } catch (error) {
      console.error('Error loading GRNs:', error);
    }
  };

  const handleCancelPO = async () => {
    if (!cancelReason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/purchase-orders/${po_id}/cancel`, {
        reason: cancelReason,
        cancelled_by: localStorage.getItem('user_id') || ''
      });
      setCancelDialogOpen(false);
      setCancelReason('');
      loadPO();
    } catch (error) {
      console.error('Error cancelling PO:', error);
      setError('Failed to cancel purchase order');
    }
  };

  const handleReceiveGoods = () => {
    navigate(`/purchase-orders/${po_id}/receive`);
  };

  const handleOpenPaymentDialog = () => {
    const currentBalance = po.balance || po.grand_total || 0;
    setPaymentData({
      amount_paid: currentBalance,
      payment_mode: 'cash',
      payment_notes: ''
    });
    setPaymentError('');
    setPaymentDialogOpen(true);
  };

  const handleUpdatePayment = async () => {
    setPaymentError('');
    if (!paymentData.amount_paid || paymentData.amount_paid <= 0) {
      setPaymentError('Please enter a valid payment amount');
      return;
    }
    const currentBalance = po.balance || po.grand_total || 0;
    if (paymentData.amount_paid > currentBalance) {
      setPaymentError(`Payment amount cannot exceed balance due (₹${currentBalance.toFixed(2)})`);
      return;
    }
    if (!paymentData.payment_mode) {
      setPaymentError('Please select a payment mode');
      return;
    }
    try {
      await axios.put(`${API_BASE_URL}/purchase-orders/${po_id}/payment`, {
        amount_paid: parseFloat(paymentData.amount_paid),
        payment_mode: paymentData.payment_mode,
        payment_notes: paymentData.payment_notes,
        updated_by: localStorage.getItem('user_id') || ''
      });
      setPaymentDialogOpen(false);
      setPaymentData({ amount_paid: 0, payment_mode: 'cash', payment_notes: '' });
      loadPO();
    } catch (error) {
      console.error('Error updating payment:', error);
      setPaymentError(error.response?.data?.error || 'Failed to update payment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      CREATED: 'default', SENT: 'info', PARTIALLY_RECEIVED: 'warning',
      RECEIVED: 'success', CANCELLED: 'error'
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    const colors = { unpaid: 'error', partial: 'warning', paid: 'success' };
    return colors[status] || 'default';
  };

  const calculateProgress = () => {
    if (!po || !po.items) return 0;
    const totalOrdered = po.items.reduce((sum, item) => sum + item.quantity_ordered, 0);
    const totalReceived = po.items.reduce((sum, item) => sum + (item.quantity_received || 0), 0);
    return totalOrdered > 0 ? (totalReceived / totalOrdered) * 100 : 0;
  };

  const DRAWER_WIDTH = '300px';

  if (loading || !po) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', ml: { xs: 0, md: DRAWER_WIDTH } }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  const canReceiveGoods = ['CREATED', 'SENT', 'PARTIALLY_RECEIVED'].includes(po.status);
  const canCancel = !['RECEIVED', 'CANCELLED'].includes(po.status);
  const progress = calculateProgress();
  const canUpdatePayment = po.payment_status !== 'paid' && po.status !== 'CANCELLED';
  const currentBalance = po.balance || po.grand_total || 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', ml: { xs: 0, md: DRAWER_WIDTH }, p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/purchase-orders')} sx={{ mr: 2, bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#f5f5f5' } }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" fontWeight={600} color="primary">{po.po_number}</Typography>
              <Chip label={po.status.replace(/_/g, ' ')} color={getStatusColor(po.status)} sx={{ fontWeight: 600 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Created on {new Date(po.po_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} • By {po.created_by_name || 'System'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {canReceiveGoods && (
            <Button variant="contained" color="success" size="large" startIcon={<ShippingIcon />} onClick={handleReceiveGoods}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: "#006D77", px: 3 }}>
              Receive Goods
            </Button>
          )}
          {canCancel && (
            <Button variant="outlined" color="error" size="large" startIcon={<CancelIcon />} onClick={() => setCancelDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
              Cancel PO
            </Button>
          )}
          <Button variant="outlined" size="large" startIcon={<PrintIcon />} onClick={() => window.print()}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Print</Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {po.status === 'PARTIALLY_RECEIVED' && (
        <Alert severity="warning" icon={<PendingIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Partially Received</Typography>
          <Typography variant="body2">
            This order has been partially received. {po.items?.filter(item => !item.is_received).length} items are still pending delivery.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Progress</Typography>
              <Typography variant="caption" fontWeight={600}>{progress.toFixed(0)}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
          </Box>
        </Alert>
      )}
      {po.status === 'CANCELLED' && (
        <Alert severity="error" icon={<CancelIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Purchase Order Cancelled</Typography>
          {po.cancel_reason && <Typography variant="body2">Reason: {po.cancel_reason}</Typography>}
        </Alert>
      )}
      {canUpdatePayment && currentBalance > 0 && (
        <Alert severity="warning" icon={<MoneyIcon />} sx={{ mb: 3, borderRadius: 2 }}
          action={<Button color="inherit" size="small" startIcon={<PaymentIcon />} onClick={handleOpenPaymentDialog} sx={{ fontWeight: 600 }}>Update Payment</Button>}>
          <Typography variant="subtitle2" fontWeight={600}>Payment Pending</Typography>
          <Typography variant="body2">
            Outstanding balance: ₹{currentBalance.toFixed(2)} • Current Status: {po.payment_status?.toUpperCase() || 'UNPAID'}
          </Typography>
        </Alert>
      )}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Main Details */}
        <Grid item xs={12} md={8}>
          {/* PO Summary Card */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Purchase Order Information
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                      <ReceiptIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      PO Number
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                      {po.po_number}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                      <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      PO Date
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                      {new Date(po.po_date).toLocaleDateString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                      <ShippingIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Expected Delivery
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {po.expected_delivery_date ? 
                        new Date(po.expected_delivery_date).toLocaleDateString('en-IN') : 
                        'Not specified'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                      <MoneyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Payment Terms
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {po.payment_terms || 'Not specified'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Items Table */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Order Items
                </Typography>
                <Chip 
                  label={`${po.items?.length || 0} Items`} 
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Medicine Details</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Ordered</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Received</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Pending</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {po.items?.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.medicine_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.generic_name}
                          </Typography>
                          {item.batch_number && (
                            <Chip 
                              label={`Batch: ${item.batch_number}`}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5, fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.quantity_ordered}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.quantity_received || 0}
                            size="small"
                            color={item.is_received ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            color={item.quantity_pending > 0 ? 'warning.main' : 'text.secondary'}
                          >
                            {item.quantity_pending || (item.quantity_ordered - (item.quantity_received || 0))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            ₹{(item.unit_price || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="primary">
                            ₹{(item.total || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {item.is_received ? (
                            <Chip 
                              icon={<CheckCircleIcon />}
                              label="Received" 
                              size="small" 
                              color="success"
                            />
                          ) : (
                            <Chip 
                              icon={<PendingIcon />}
                              label="Pending" 
                              size="small" 
                              color="warning"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* GRNs Section */}
          {grns.length > 0 && (
            <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Goods Receipt Notes
                  </Typography>
                  <Chip 
                    label={`${grns.length} GRN${grns.length > 1 ? 's' : ''}`}
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                {grns.map((grn, index) => (
                  <Card
                    key={grn.id}
                    variant="outlined"
                    sx={{
                      mb: index < grns.length - 1 ? 2 : 0,
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            GRN Number
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {grn.grn_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Received Date
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {new Date(grn.received_date).toLocaleDateString('en-IN')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Invoice Number
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {grn.invoice_number || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Quantity
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={grn.total_quantity_received}
                              size="small"
                              color="success"
                            />
                            <Typography variant="caption">of {grn.total_quantity_ordered}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Sidebar Info */}
        <Grid item xs={12} md={4}>
          {/* Distributor Info */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Distributor Details
                </Typography>
              </Box>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f8f9fa', 
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  {po.distributor_name}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    📞 {po.distributor_contact}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ✉️ {po.distributor_email}
                  </Typography>
                  {po.distributor_gst && (
                    <Typography variant="body2" color="text.secondary">
                      🏢 GST: {po.distributor_gst}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {/* <MoneyIcon sx={{ mr: 1, color: 'primary.main' }} /> */}
                <Typography variant="h6" fontWeight={600}>
                  Financial Summary
                </Typography>
              </Box>

              <Box sx={{ 
                p: 2, 
                bgcolor: '#f8f9fa', 
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{(po.subtotal || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Discount:</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    -₹{(po.total_discount || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">GST (12%):</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{(po.total_gst || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Shipping:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{(po.shipping_charges || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight={600}>
                    Grand Total:
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="primary">
                    ₹{(po.grand_total || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          
          {/*  PAYMENT HISTORY CARD */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>Payment Information</Typography>
                </Box>
                {canUpdatePayment && currentBalance > 0 && (
                  <Tooltip title="Update Payment">
                    <IconButton size="small" color="primary" onClick={handleOpenPaymentDialog}>
                      <PaymentIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Status:</Typography>
                  <Chip label={(po.payment_status || 'unpaid').toUpperCase()} size="small"
                    color={getPaymentStatusColor(po.payment_status)} sx={{ fontWeight: 600 }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Grand Total:</Typography>
                  <Typography variant="body2" fontWeight={600}>₹{(po.grand_total || 0).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Total Paid:</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">₹{(po.amount_paid || 0).toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body1" fontWeight={600}>Balance Due:</Typography>
                  <Typography variant="body1" fontWeight={600} color={currentBalance > 0 ? 'error.main' : 'success.main'}>
                    ₹{currentBalance.toFixed(2)}
                  </Typography>
                </Box>

                {/*  PAYMENT HISTORY */}
                {po.payment_history && po.payment_history.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <PaymentIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Payment History ({po.payment_history.length})
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 1.5 }}>
                      {po.payment_history.map((payment, index) => (
                        <Card key={index} variant="outlined"
                          sx={{ mb: 1.5, bgcolor: 'white', borderLeft: '3px solid', borderLeftColor: 'success.main', '&:hover': { boxShadow: 1 } }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Grid container spacing={1.5}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Amount</Typography>
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  ₹{(payment.amount || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Mode</Typography>
                                <Chip label={payment.payment_mode?.toUpperCase() || 'N/A'} size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }} />
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Date</Typography>
                                <Typography variant="body2" fontSize="0.8rem">
                                  {new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                    day: '2-digit', month: 'short', year: 'numeric'
                                  })}
                                </Typography>
                              </Grid>
                              {payment.grn_number && (
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">GRN</Typography>
                                  <Typography variant="body2" fontSize="0.8rem">{payment.grn_number}</Typography>
                                </Grid>
                              )}
                              {payment.payment_notes && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                                  <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                                    {payment.payment_notes}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </>
                )}

                {canUpdatePayment && currentBalance > 0 && (
                  <Button variant="contained" fullWidth startIcon={<PaymentIcon />} onClick={handleOpenPaymentDialog}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, mt: 2 }}>
                    Record Payment
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Cancel Purchase Order</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
            <Typography variant="body2">This action cannot be undone. Please provide a reason for cancellation.</Typography>
          </Alert>
          <TextField autoFocus margin="dense" label="Cancellation Reason" fullWidth multiline rows={4}
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Please provide a detailed reason for cancelling this purchase order..." required />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setCancelDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
          <Button onClick={handleCancelPO} variant="contained" color="error" disabled={!cancelReason.trim()}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Confirm Cancellation</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />Record Payment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1, borderRadius: 2 }}>
            <Typography variant="body2" gutterBottom><strong>Grand Total:</strong> ₹{(po.grand_total || 0).toFixed(2)}</Typography>
            <Typography variant="body2" gutterBottom><strong>Already Paid:</strong> ₹{(po.amount_paid || 0).toFixed(2)}</Typography>
            <Typography variant="body2"><strong>Balance Due:</strong> ₹{currentBalance.toFixed(2)}</Typography>
          </Alert>
          {paymentError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{paymentError}</Alert>}
          <TextField label="Payment Amount" fullWidth type="number" value={paymentData.amount_paid}
            onChange={(e) => setPaymentData({ ...paymentData, amount_paid: parseFloat(e.target.value) || 0 })}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            inputProps={{ min: 0, max: currentBalance, step: 0.01 }} sx={{ mb: 2 }} required />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment Mode</InputLabel>
            <Select value={paymentData.payment_mode} label="Payment Mode"
              onChange={(e) => setPaymentData({ ...paymentData, payment_mode: e.target.value })}>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="credit">Credit</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Payment Notes (Optional)" fullWidth multiline rows={3} value={paymentData.payment_notes}
            onChange={(e) => setPaymentData({ ...paymentData, payment_notes: e.target.value })}
            placeholder="Add any additional payment details..." />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setPaymentDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleUpdatePayment} variant="contained" color="primary"
            disabled={!paymentData.amount_paid || paymentData.amount_paid <= 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Record Payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderDetail;